import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { handleFetch } from "src/api";
import { endPoints } from "src/shared/model";

type ContactPayload = {
  name: string;
  email: string;
  company: string;
  message: string;
};

type ContactResponse = {
  ok: boolean;
  error?: string;
};

type SubmitStatus = "idle" | "sending" | "success" | "error";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Chrome/Safari paint autofilled inputs with a light-blue background and
// black text, which breaks the dark form. Repaint them to match the theme.
const autofillSx = {
  // Typed text should be white on the dark form (theme text.primary is 70% grey)
  "& .MuiInputBase-input": {
    color: "#ffffff",
  },
  "& input:-webkit-autofill, & textarea:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 1000px #000000 inset",
    WebkitTextFillColor: "#ffffff",
    caretColor: "#ffffff",
    borderRadius: "inherit",
    transition: "background-color 5000s ease-in-out 0s",
  },
};

const labelSlotProps = {
  inputLabel: {
    sx: {
      "&.Mui-focused": {
        color: "primary.main",
      },
    },
  },
};

export const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorText, setErrorText] = useState<string>("");
  const [emailTouched, setEmailTouched] = useState<boolean>(false);

  const isSending = status === "sending";
  const isEmailValid = EMAIL_REGEX.test(email.trim());
  const showEmailError = emailTouched && email.length > 0 && !isEmailValid;

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleChangeCompany = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompany(event.target.value);
  };

  const handleChangeMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSending) return;

    if (!isEmailValid) {
      setEmailTouched(true);
      return;
    }

    setStatus("sending");
    setErrorText("");

    try {
      const response = await handleFetch<ContactResponse, ContactPayload>(
        endPoints.contact,
        "POST",
        {
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          message: message.trim(),
        },
      );

      if (response?.ok) {
        setStatus("success");
        setName("");
        setEmail("");
        setCompany("");
        setMessage("");
        setEmailTouched(false);
      } else {
        setStatus("error");
        setErrorText(response?.error || t("contactForm.error"));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Contact form submit failed:", error);
      setStatus("error");
      setErrorText(t("contactForm.error"));
    }
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      noValidate
      width={1}
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      border={1}
      borderRadius={4}
      borderColor="secondary.main"
      padding={4}
    >
      <TextField
        name="name"
        autoComplete="name"
        label={t("contactForm.name")}
        variant="outlined"
        color="secondary"
        placeholder={t("contactForm.namePlaceholder")}
        size={isMedium ? "small" : "medium"}
        onChange={handleChangeName}
        value={name}
        disabled={isSending}
        fullWidth
        required
        focused
        sx={autofillSx}
        slotProps={labelSlotProps}
      />
      <TextField
        name="email"
        type="email"
        autoComplete="email"
        label={t("contactForm.email")}
        variant="outlined"
        color="secondary"
        placeholder={t("contactForm.emailPlaceholder")}
        size={isMedium ? "small" : "medium"}
        onChange={handleChangeEmail}
        onBlur={() => setEmailTouched(true)}
        value={email}
        disabled={isSending}
        error={showEmailError}
        helperText={showEmailError ? t("contactForm.invalidEmail") : undefined}
        fullWidth
        required
        focused
        sx={autofillSx}
        slotProps={labelSlotProps}
      />
      <TextField
        name="company"
        autoComplete="organization"
        label={t("contactForm.company")}
        variant="outlined"
        color="secondary"
        placeholder={t("contactForm.companyPlaceholder")}
        size={isMedium ? "small" : "medium"}
        onChange={handleChangeCompany}
        value={company}
        disabled={isSending}
        fullWidth
        focused
        sx={autofillSx}
        slotProps={labelSlotProps}
      />
      <TextField
        name="message"
        label={t("contactForm.message")}
        variant="outlined"
        color="secondary"
        placeholder={t("contactForm.messagePlaceholder")}
        onChange={handleChangeMessage}
        value={message}
        disabled={isSending}
        fullWidth
        required
        focused
        multiline
        minRows={isMedium ? 3 : 4}
        maxRows={isMedium ? 6 : 8}
        sx={autofillSx}
        slotProps={labelSlotProps}
      />

      {status === "success" && (
        <Alert severity="success" variant="outlined" sx={{ width: 1 }}>
          {t("contactForm.success")}
        </Alert>
      )}
      {status === "error" && (
        <Alert severity="error" variant="outlined" sx={{ width: 1 }}>
          {errorText || t("contactForm.error")}
        </Alert>
      )}

      <Button
        type="submit"
        color="primary"
        variant="contained"
        size={isMedium ? "small" : "large"}
        disabled={!name.trim() || !email.trim() || !message.trim() || isSending}
        startIcon={
          isSending ? <CircularProgress size={16} color="inherit" /> : undefined
        }
        sx={{
          textTransform: "none",
          color: "common.black",
        }}
      >
        {isSending ? t("contactForm.sending") : t("contactForm.submit")}
      </Button>
    </Stack>
  );
};
